// src/components/shared/NewsBar.tsx
// DROP 40B — ERUE Festival Suite Scrolling News Ticker
// Fetches breaking news from /api/public/news/breaking
// Auto-refreshes every 5 minutes

import React, { useState, useEffect, useRef } from "react";

interface BreakingItem {
  id: string;
  title: string;
  category: string;
  village?: string;
  emoji?: string;
}

interface Props {
  refreshInterval?: number; // ms — default 300000 (5min)
}

export default function NewsBar({ refreshInterval = 300_000 }: Props) {
  const [items,   setItems]   = useState<BreakingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [paused,  setPaused]  = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const fetchBreaking = async () => {
    try {
      const res  = await fetch("/api/public/news/breaking");
      const data = await res.json();
      if (data.success && Array.isArray(data.items) && data.items.length > 0) {
        setItems(data.items);
      } else {
        // Default items when no breaking news set
        setItems([
          { id: "d1", emoji: "👑", category: "Royal Announcement", title: "ERUE Festival 2026 — December 24–30, Uzairue Kingdom, Etsako West LGA, Edo State" },
          { id: "d2", emoji: "🎉", category: "Festival Alert",      title: "Grand Finale: Wednesday 30 December 2026 — Palace of the Ogieneni, Jattu-Uzairue" },
          { id: "d3", emoji: "🗳️", category: "Miss ERUE",          title: "Miss ERUE Contestant Registration NOW OPEN — Register at the Participate menu" },
          { id: "d4", emoji: "🏪", category: "Vendors",             title: "Festival Store & Vendor Registration Open — All 22 villages and component communities welcome" },
          { id: "d5", emoji: "🎟️", category: "Raffle",             title: "ERUE Festival Raffle — WIN amazing prizes! Buy your lucky ticket today" },
        ]);
      }
    } catch {
      setItems([
        { id: "f1", emoji: "👑", category: "ERUE Festival 2026", title: "December 24–30 — Uzairue Kingdom — One People, One Kingdom, One Destiny" },
      ]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchBreaking();
    const interval = setInterval(fetchBreaking, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  if (loading || items.length === 0) return null;

  // Duplicate for seamless loop
  const allItems = [...items, ...items];

  return (
    <div
      style={{
        background: "linear-gradient(90deg, #0F5132 0%, #0a3d24 40%, #0F5132 100%)",
        borderBottom: "1px solid #C9A84C44",
        borderTop: "1px solid #C9A84C22",
        height: 34,
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        position: "relative",
        zIndex: 40,
        userSelect: "none",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* LIVE label */}
      <div style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "0 14px",
        background: "#C9A84C",
        height: "100%",
        zIndex: 2,
        borderRight: "1px solid #C9A84C66",
      }}>
        <span style={{
          width: 7, height: 7, borderRadius: "50%",
          background: "#0F5132",
          animation: "newsbar-pulse 1.4s ease-in-out infinite",
          display: "inline-block",
          flexShrink: 0,
        }}/>
        <span style={{
          fontSize: 10,
          fontWeight: 900,
          color: "#070D0A",
          letterSpacing: 2,
          textTransform: "uppercase",
          fontFamily: "Plus Jakarta Sans, sans-serif",
          whiteSpace: "nowrap",
        }}>
          LIVE NEWS
        </span>
      </div>

      {/* Scrolling track */}
      <div style={{ flex: 1, overflow: "hidden", position: "relative", height: "100%" }}>
        <div
          ref={trackRef}
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            whiteSpace: "nowrap",
            animation: paused
              ? "newsbar-scroll 0s linear infinite paused"
              : `newsbar-scroll ${Math.max(40, items.length * 12)}s linear infinite`,
            willChange: "transform",
          }}
        >
          {allItems.map((item, i) => (
            <span key={`${item.id}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
              <span style={{
                fontSize: 11,
                color: "#EDE8DC",
                fontFamily: "Plus Jakarta Sans, sans-serif",
                padding: "0 8px",
              }}>
                <span style={{ marginRight: 4 }}>{item.emoji || "📰"}</span>
                <span style={{
                  color: "#C9A84C",
                  fontWeight: 700,
                  fontSize: 9,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  marginRight: 6,
                }}>
                  {item.category}
                </span>
                {item.title}
                {item.village && item.village !== "Uzairue Kingdom" && (
                  <span style={{ color: "#4ADE80", fontSize: 9, marginLeft: 6 }}>
                    [{item.village}]
                  </span>
                )}
              </span>
              <span style={{
                color: "#C9A84C",
                fontSize: 14,
                padding: "0 12px",
                opacity: 0.6,
                flexShrink: 0,
              }}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* Pause indicator */}
      {paused && (
        <div style={{
          position: "absolute", right: 10, top: "50%",
          transform: "translateY(-50%)",
          fontSize: 9, color: "#C9A84C99",
          fontFamily: "Plus Jakarta Sans, sans-serif",
          letterSpacing: 1,
          pointerEvents: "none",
        }}>
          ⏸ PAUSED
        </div>
      )}

      <style>{`
        @keyframes newsbar-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes newsbar-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
}
